/**
 * "Ann solved Magic 18 in 12:34 · 3rd on this puzzle" — the last solves across the tenant,
 * beside the puzzle list (ADR 0043).
 *
 * Built from leaderboard rows and shown under the leaderboard's rule: pseudonym, points,
 * time, rank, date, and nothing that hints at a solution. Pushed over the socket as
 * `activity.changed`; the event carries no row, so the feed refetches, like a board.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/core/ui";
import { useRealtimeOpen, useTopic } from "@/core/realtime/RealtimeProvider";
import { ACTIVITY_TOPIC } from "@/core/realtime/protocol";
import { formatDuration, formatRelativeTime } from "@/core/play/time";
import { useRecentSolves, type RecentSolve } from "./api";

/** The current minute, so "2 minutes ago" keeps up without a refetch. */
function useNow(everyMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(id);
  }, [everyMs]);
  return now;
}

export function ActivityFeed() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const live = useRealtimeOpen();
  const feed = useRecentSolves({ live });
  const now = useNow(60_000);
  useTopic(ACTIVITY_TOPIC, (frame) => {
    if (frame.type !== "activity.changed") return;
    void qc.invalidateQueries({ queryKey: ["activity"] });
  });
  const solves = feed.data?.solves ?? [];
  return (
    <aside className="bt-activity" aria-labelledby="bt-activity-title">
      <h2 id="bt-activity-title" className="bt-activity__title">
        {t("activity.title")}
      </h2>
      {!feed.isPending && solves.length === 0 ? (
        <p className="bt-activity__empty">{t("activity.empty")}</p>
      ) : null}
      {solves.length > 0 ? (
        <ol className="bt-activity__list">
          {solves.map((solve) => (
            <ActivityItem
              key={`${solve.puzzle.slug}:${solve.participant}:${solve.submitted_at}`}
              solve={solve}
              now={now}
            />
          ))}
        </ol>
      ) : null}
    </aside>
  );
}

function ActivityItem({ solve, now }: { solve: RecentSolve; now: number }) {
  const { t, i18n } = useTranslation();
  // A team solve keeps its name even for a member: "You" would hide who actually played.
  const mine = solve.is_me && !solve.is_team;
  return (
    <li className={cn("bt-activity__item", solve.is_me && "bt-activity__item--me")}>
      <p className="bt-activity__line">
        <Trans
          i18nKey={mine ? "activity.solved_me" : "activity.solved"}
          values={{
            participant: solve.participant,
            puzzle: solve.puzzle.title,
            time: formatDuration(solve.elapsed_ms),
          }}
          components={{
            who: <strong className="bt-activity__who" />,
            puzzle: <Link to={`/puzzles/${solve.puzzle.slug}`} className="bt-activity__puzzle" />,
            time: <span className="bt-activity__time" />,
          }}
        />
        {solve.is_team ? (
          <>
            {" "}
            <span className="bt-activity__team-tag">({t("activity.team")})</span>
          </>
        ) : null}
      </p>
      <p className="bt-activity__detail">
        {solve.scored && solve.score !== null ? (
          <span className="bt-activity__points">
            {t("activity.points", { count: solve.score })}
          </span>
        ) : null}
        <span className="bt-activity__rank">
          {t("activity.rank", {
            rank: t("play.rank_ordinal", { count: solve.rank, ordinal: true }),
          })}
        </span>
        <time
          className="bt-activity__when"
          dateTime={solve.submitted_at}
          title={new Date(solve.submitted_at).toLocaleString(i18n.language)}
        >
          {formatRelativeTime(solve.submitted_at, now, i18n.language)}
        </time>
      </p>
    </li>
  );
}
